var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var _a;
import { Group } from '@/models/Group';
import ErrorHandler from '@/support/ErrorHandler';
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import MiCalendar from '@/components/mi-calendar/MiCalendar';
import { defaultCalendarSettings } from '@/support/CalendarDefaults';
import ExpandableGoalItem from '@/views/Groups/Tabs/GoalPlanner/ExpandableGoalItem/ExpandableGoalItem.vue';
import { DateTime } from 'luxon';
import AddTemplateDialog from '@/views/Groups/Tabs/GoalPlanner/AddTemplateDialog/AddTemplateDialog.vue';
import { RpcClient } from '@/http';
import { GroupSubject, GroupSubjectIncludes } from '@/models/GroupSubject';
import { Curriculum as CurriculumModel } from '@/models/Curriculum';
import { sortByKey } from '@/support/Object';
import { formatTimeslotToCalendarEvent } from '@/support/GoalPlanner';
import ManageTimeslotDialog from '@/views/Groups/Tabs/GoalPlanner/ManageTimeslotDialog/ManageTimeslotDialog.vue';
import AExpandableFilter from '@/components/AExpandableFilter/AExpandableFilter.vue';
import AreaFilter from '@/components/SummaryFilters/AreaFilter/AreaFilter.vue';
import TopicSelect from '@/components/TopicSelect/TopicSelect.vue';
import SelectFilter from '@/components/select-filter/SelectFilter.vue';
import SubjectLabelSelect from '@/components/SubjectLabelSelect/SubjectLabelSelect.vue';
import { debounce } from 'lodash';
import ActionsMenu from '@/components/ActionsMenu/ActionsMenu.vue';
import ALoader from '@/components/ALoader/ALoader.vue';
import GoalPlannerNotes from '@/views/Groups/Tabs/GoalPlanner/GoalPlannerNotes/GoalPlannerNotes.vue';
import BirthdayList from '@/components/BirthdayList/BirthdayList.vue';
import GoalPlannerLogbookDialog from './GoalPlannerLogbookDialog/GoalPlannerLogbookDialog';
let GoalPlanner = class GoalPlanner extends Vue {
    constructor() {
        // #region @VModel
        // #endregion
        super(...arguments);
        // #endregion
        // #region @Refs
        // #endregion
        // #region Class properties
        this.isLoading = true;
        this.isDownloading = false;
        this.isLoadingGroupSubjects = false;
        // Calendar
        this.calendarApi = null;
        this.calendarActiveStart = '';
        this.currentView = this.initialView;
        this.groupTemplates = null;
        // Filters
        this.filters = {
            area: '',
            topic: '',
            levels: [],
            periods: [],
            subjectLabels: [],
            withAssigned: 0,
        };
        // Curriculum & Subjects
        this.curriculum = new CurriculumModel();
        this.subjects = [];
        this.debouncedFetchGroupSubjects = debounce(this.fetchGroupSubjects, 100);
        // Dialogs
        this.addTemplateDialogIsOpen = false;
        this.addTemplateDialogType = 'week';
        this.logBookDialogIsOpen = false;
        this.manageTimeslotDialogIsOpen = false;
        this.selectedTimeslot = null;
        this.dateSelectedTimeslot = null;
        // #endregion
    }
    // #endregion
    // #region Lifecycle Hooks
    mounted() {
        this.initialize();
    }
    async initialize() {
        this.isLoading = true;
        this.$store.dispatch('clearSelectedGroupSubjectIds');
        await this.fetchCurriculum();
        await this.fetchGroupSubjects();
        this.isLoading = false;
        await this.$nextTick();
        const calendarApi = this.$refs.calendar;
        if (!calendarApi) {
            return;
        }
        this.calendarApi = calendarApi;
    }
    beforeDestroy() {
        this.$store.dispatch('clearExpandableGoalItemsState');
    }
    // #endregion
    // #region Event handlers
    onOpenAddTemplateDialog(type) {
        this.addTemplateDialogType = type;
        this.addTemplateDialogIsOpen = true;
    }
    onCloseAddTemplateDialog() {
        this.addTemplateDialogIsOpen = false;
    }
    onOpenLogBook() {
        this.logBookDialogIsOpen = true;
    }
    onSubjectDropped(payload) {
        const timeslotId = payload.timeslotId;
        const subjectIds = payload.subjects;
        const date = payload.date;
        if (!timeslotId || !subjectIds || !date) {
            return;
        }
        this.updateTimeslot(timeslotId, subjectIds, date);
    }
    onOpenManageTimeslotDialog() {
        if (!this.selectedTimeslot || !this.dateSelectedTimeslot) {
            return;
        }
        this.manageTimeslotDialogIsOpen = true;
    }
    onCloseManageTimeslotDialog() {
        this.manageTimeslotDialogIsOpen = false;
        this.selectedTimeslot = null;
        this.dateSelectedTimeslot = null;
    }
    // #endregion
    // #region Class methods
    findTimeslotById(date, id) {
        if (!this.groupTemplates) {
            return null;
        }
        const timeslots = this.groupTemplates[date];
        if (!timeslots || !timeslots.length) {
            return null;
        }
        const timeslot = timeslots.find((slot) => {
            if (!slot) {
                return false;
            }
            return slot.id === id;
        });
        return timeslot || null;
    }
    reinitializeExpandableItems() {
        const findAndReinitialize = (components) => {
            components.forEach((component) => {
                if (component.$options.name === 'expandable-goal-item' &&
                    typeof component.reinitialize === 'function') {
                    component.reinitialize();
                }
                if (component.$children && component.$children.length > 0) {
                    findAndReinitialize(component.$children);
                }
            });
        };
        findAndReinitialize(this.$children);
    }
    // #endregion
    // #region Async methods (api calls only)
    async downloadPdf() {
        try {
            const response = await new RpcClient().execute('get-template-for-group', {
                group_id: this.group.id,
                starts_at: this.activeStart,
                type: 'week',
                // type: this.isDayView ? 'day' : 'week',
                download: true,
            });
            if (response) {
                window.open(response || '', '_blank');
            }
        }
        catch (error) {
            ErrorHandler.network(error);
        }
    }
    async onRemovedGoal() {
        await this.fetchGroupTemplates();
        await this.fetchGroupSubjects();
    }
    async onRemovedTimeslot() {
        this.manageTimeslotDialogIsOpen = false;
        this.selectedTimeslot = null;
        this.dateSelectedTimeslot = null;
        await this.fetchGroupTemplates();
    }
    async updateTimeslot(timeslotId, subjects, date) {
        try {
            await new RpcClient().execute('add-subjects-to-timeslot-for-group', {
                group_id: this.group.id,
                timeslot_id: timeslotId,
                subjects,
                date,
            });
            await Promise.all([this.fetchGroupSubjects(), this.fetchGroupTemplates()]);
        }
        catch (error) {
            ErrorHandler.network(error);
        }
    }
    async fetchGroupTemplates() {
        try {
            this.groupTemplates = await new RpcClient().execute('get-template-for-group', {
                group_id: this.group.id,
                starts_at: this.activeStart,
                type: 'week',
            });
            await this.$nextTick();
            this.reinitializeExpandableItems();
        }
        catch (error) {
            ErrorHandler.network(error);
        }
    }
    async fetchCurriculum() {
        try {
            this.curriculum = await this.curriculum.current().include(['children', 'subjects']).first();
            const areas = this.curriculum.subjects || [];
            areas.forEach((area, index) => {
                this.curriculum.subjects[index] = this.curriculum.dataToModel(area, 'Subject');
                const topics = this.curriculum.subjects[index].children || [];
                topics.forEach((topic, i) => {
                    this.curriculum.subjects[index].children[i] = this.curriculum.dataToModel(topic, 'Subject');
                });
                topics.sort(sortByKey('name'));
                this.$set(this.curriculum.subjects[index], 'children', topics);
            });
            areas.sort(sortByKey('name'));
            this.$set(this.curriculum.subjects, 'subjects', areas);
        }
        catch (error) {
            ErrorHandler.network(error);
        }
    }
    async fetchGroupSubjects() {
        this.isLoadingGroupSubjects = true;
        const filter = {};
        if (this.filters) {
            if (this.filters.area) {
                filter.areas = [this.filters.area];
            }
            if (this.filters.topic) {
                filter.topics = [this.filters.topic];
            }
            if (this.filters.levels) {
                filter.levels = this.filters.levels;
            }
            if (this.filters.periods) {
                filter.periods = this.filters.periods;
            }
            if (this.filters.subjectLabels && this.filters.subjectLabels.length) {
                filter.subject_labels = this.filters.subjectLabels;
            }
            if (this.filters.withAssigned !== undefined && this.filters.withAssigned !== null) {
                filter.with_assigned = this.filters.withAssigned ? 1 : 0;
            }
        }
        try {
            this.subjects = await new GroupSubject()
                .filter(filter)
                .group(this.group.id)
                .include([
                GroupSubjectIncludes.LEVEL,
                GroupSubjectIncludes.PARENT,
                GroupSubjectIncludes.PERIOD,
                GroupSubjectIncludes.SUBJECT_LABELS,
            ])
                .all();
        }
        catch (error) {
            ErrorHandler.network(error);
        }
        finally {
            this.isLoadingGroupSubjects = false;
        }
    }
    async onTemplateAdded() {
        this.addTemplateDialogIsOpen = false;
        await this.fetchGroupTemplates();
        await this.fetchGroupSubjects();
    }
    // #endregion
    // #region Getters & Setters
    get calendarOptions() {
        return {
            ...defaultCalendarSettings,
            ...{
                timezone: 'local',
                editable: false,
                selectable: false,
                buttonText: {
                    today: this.$t('rosterManagement.today'),
                },
                header: {
                    left: 'title',
                    right: 'timeGridDay,timeGridWeek, prev, today, next',
                },
                firstDay: 1,
                weekends: false,
                eventOverlap: false,
                defaultView: this.initialView,
                minTime: '08:00:00',
                maxTime: '18:00:00',
                slotDuration: '00:05:00',
                eventClick: (selection) => {
                    const event = selection.event;
                    if (!event || !event.id) {
                        return;
                    }
                    const date = DateTime.fromJSDate(event.start).toISODate();
                    this.selectedTimeslot = this.findTimeslotById(date, event.id);
                    this.dateSelectedTimeslot = date;
                    this.onOpenManageTimeslotDialog();
                },
                viewSkeletonRender: (info) => {
                    this.currentView = info.view.type;
                },
                datesRender: (info) => {
                    const newStart = DateTime.fromJSDate(info.view.activeStart).toISODate();
                    if (this.calendarActiveStart !== newStart) {
                        this.calendarActiveStart = newStart;
                    }
                },
                eventRender: (info) => {
                    // Add draggable class & data attributes to calendar event
                    info.el.classList.add('calendar-event-droppable');
                    info.el.setAttribute('data-timeslot-id', info.event.id);
                    const eventStart = DateTime.fromJSDate(info.event.start).toISODate();
                    info.el.setAttribute('data-date', eventStart);
                    const extendedProps = info.event.extendedProps;
                    if (extendedProps) {
                        const backgroundColor = info.event.extendedProps.customColor || '#F9A825';
                        info.el.style.backgroundColor = backgroundColor;
                        if (backgroundColor === '#FFEE58' || backgroundColor === '#64FFDA') {
                            info.el.style.color = 'black';
                        }
                        else {
                            info.el.style.color = 'white';
                        }
                        if (extendedProps.subject_count) {
                            info.el.classList.add('calendar-event-with-subjects');
                            const desc = document.createElement('div');
                            desc.className = 'fc-event-description event-subject-count';
                            desc.innerText =
                                extendedProps.subject_count === 1
                                    ? this.$tc('1 goal added', 0, { item: extendedProps.subject_count })
                                    : this.$tc('{item} goals added', 0, { item: extendedProps.subject_count });
                            info.el.appendChild(desc);
                        }
                    }
                },
            },
        };
    }
    get calendarEvents() {
        if (!this.groupTemplates) {
            return [];
        }
        return this.isDayView
            ? this.formattedTimeslotsForDayTemplate
            : this.formattedTimeslotsForWeekTemplate;
    }
    get formattedTimeslotsForWeekTemplate() {
        if (!this.groupTemplates) {
            return [];
        }
        return Object.keys(this.groupTemplates).flatMap((day) => {
            const timeslots = this.groupTemplates[day] || [];
            return timeslots.map((timeslot) => formatTimeslotToCalendarEvent(timeslot, day));
        });
    }
    get formattedTimeslotsForDayTemplate() {
        if (!this.groupTemplates) {
            return [];
        }
        const timeslots = this.groupTemplates[this.calendarActiveStart];
        if (!timeslots || !timeslots.length) {
            return [];
        }
        return timeslots.map((timeslot) => formatTimeslotToCalendarEvent(timeslot, this.calendarActiveStart));
    }
    get isDayView() {
        return this.currentView === 'timeGridDay';
    }
    get activeStart() {
        const date = DateTime.fromISO(this.calendarActiveStart) || DateTime.local();
        const monday = date.minus({ days: date.weekday - 1 });
        return monday.toISODate();
    }
    get hasAreaFilter() {
        return !!this.filters.area;
    }
    get topicFilters() {
        return {
            parents: [this.filters.area],
        };
    }
    get hasSubjects() {
        return !!(this.subjects && this.subjects.length);
    }
    get actionMenuActions() {
        return [
            {
                icon: 'cloud_download',
                label: 'Download',
                visible: true,
                action: async () => {
                    this.isDownloading = true;
                    await this.downloadPdf();
                    this.isDownloading = false;
                },
                disabled: false,
            },
            {
                icon: 'pending_actions',
                label: this.$tc('tool.buttons.logbook'),
                visible: true,
                action: () => {
                    this.onOpenLogBook();
                },
                disabled: false,
            }
        ];
    }
    get selectedCount() {
        return this.$store.state.selectedGroupSubjectIds.length;
    }
    get hasSelection() {
        return this.selectedCount > 0;
    }
    // #endregion
    // #region @Watchers
    onCalendarActiveStartChange(newValue, oldValue) {
        if (newValue !== oldValue) {
            this.fetchGroupTemplates();
        }
    }
    onFiltersChange() {
        this.debouncedFetchGroupSubjects();
    }
};
__decorate([
    Prop({ required: true }),
    __metadata("design:type", typeof (_a = typeof Group !== "undefined" && Group) === "function" ? _a : Object)
], GoalPlanner.prototype, "group", void 0);
__decorate([
    Prop({ default: true }),
    __metadata("design:type", Boolean)
], GoalPlanner.prototype, "isPlannable", void 0);
__decorate([
    Prop({ default: 'timeGridWeek' }),
    __metadata("design:type", String)
], GoalPlanner.prototype, "initialView", void 0);
__decorate([
    Watch('calendarActiveStart'),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [String, String]),
    __metadata("design:returntype", void 0)
], GoalPlanner.prototype, "onCalendarActiveStartChange", null);
__decorate([
    Watch('filters', { deep: true }),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
], GoalPlanner.prototype, "onFiltersChange", null);
GoalPlanner = __decorate([
    Component({
        components: {
            MiCalendar,
            ExpandableGoalItem,
            ManageTimeslotDialog,
            AddTemplateDialog,
            AExpandableFilter,
            AreaFilter,
            TopicSelect,
            SelectFilter,
            SubjectLabelSelect,
            ActionsMenu,
            ALoader,
            GoalPlannerNotes,
            BirthdayList,
            GoalPlannerLogbookDialog,
        },
    })
], GoalPlanner);
export default GoalPlanner;
// #endregion
// #region Constants
// #endregion
